/**
 * **Der Geschafft-Moment** — die Regel, wann er kommt, und der Weg dorthin.
 *
 * Liegt in `lib/` und nicht in `features/focus`, aus demselben Grund wie
 * `lib/focus`: Angefordert wird er von den **Aufgaben** (der Haken in einer
 * Liste) UND vom **Fokus** (der Haken in der Pille), und ein Feature importiert
 * kein anderes Feature. Was zu sehen ist, steht drüben in
 * `features/focus/components/DoneMoment`; hier steht nur die Regel und die
 * Zustellung.
 *
 * **Ein Modul-Ereignis, kein Provider** — dasselbe Muster wie die Toasts
 * (`showSuccessToast`). Wer anfordert, braucht dann keine Hülle um sich herum,
 * und Tests wie Stories kommen ohne Kontext aus.
 */

/** Woher der Haken kam. Er entscheidet über die Regel UND über den Weg. */
export type DoneMomentOrigin = 'focus' | 'list'

/** So wenig, wie die Regel braucht — damit auch ein Schnappschuss reicht. */
export interface DoneMomentTask {
  id: string
  rewardId?: string | null
}

/**
 * **Die Regel, an einer Stelle.**
 *
 * Der Moment gilt **genau einer** Aufgabe: Bei einer Mehrfachauswahl kommt er
 * nicht — zehn Karten hintereinander wären kein Moment, sondern eine Schranke.
 *
 * Aus dem **Fokus** kommt er immer; wer eine Viertelstunde an einer Sache saß,
 * hat ihn sich verdient, ob eine Belohnung daranhängt oder nicht. Aus einer
 * **Liste** nur mit Belohnung — sonst stünde er bei jedem Haken im Weg.
 *
 * Aufgerufen wird sie nur beim Erledigen, nie beim Haken zurück; das entscheidet
 * der Aufrufer, der den vorherigen Zustand kennt.
 */
export function willShowDoneMoment(
  task: DoneMomentTask,
  origin: DoneMomentOrigin,
  count: number,
): boolean {
  if (count !== 1) return false
  if (origin === 'focus') return true
  return !!task.rewardId
}

type Listener = (taskId: string) => void

const listeners = new Set<Listener>()
let detachIpc: (() => void) | null = null

function emit(taskId: string): void {
  for (const listener of [...listeners]) listener(taskId)
}

/**
 * Horcht auf den Moment. Solange jemand horcht, hängt auch der IPC-Kanal dran:
 * Der Haken in der **Pille** fällt in einem anderen Fenster, und von dort führt
 * nur der Main-Prozess zurück (siehe {@link requestDoneMoment}).
 */
export function subscribeDoneMoment(listener: Listener): () => void {
  listeners.add(listener)
  if (listeners.size === 1 && window.api?.receive) {
    // `receive` ist als `void | (() => void)` deklariert — die Brücke gibt das
    // Abmelden zurück, die Typdeklaration lässt beides zu.
    const off = window.api.receive('done-moment:show', (payload?: { taskId?: string }) => {
      if (payload?.taskId) emit(payload.taskId)
    })
    detachIpc = typeof off === 'function' ? off : null
  }
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && detachIpc) {
      detachIpc()
      detachIpc = null
    }
  }
}

/**
 * **Fordert den Moment an** — wenn die Regel ihn zulässt.
 *
 * Aus dem **Fokus** geht er über den Main-Prozess: Der Haken fällt im
 * Pillen-Fenster, das Modal gehört ins Hauptfenster, und zwei Renderer teilen
 * kein Modul. Aus einer **Liste** bleibt er im eigenen Fenster.
 *
 * Die Abfrage auf `window.api` ist nicht bloß Vorsicht: Im **Web** (3d) steht
 * die Pille in derselben Seite, dort ist `origin === 'focus'` trotzdem der kurze
 * Weg — und dieselbe Zeile stimmt auf beiden Plattformen.
 */
export function requestDoneMoment(
  task: DoneMomentTask,
  origin: DoneMomentOrigin,
  count: number,
): void {
  if (!willShowDoneMoment(task, origin, count)) return

  if (origin === 'focus' && window.api?.invoke) {
    void window.api.invoke('done-moment:request', { taskId: task.id })
    return
  }
  emit(task.id)
}
